import { useState, useMemo } from "react";
import { Eye, Calendar, Plus, Trash } from "lucide-react";
import dayjs from "dayjs";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ActiveConsultation } from "@/lib/dashboard/patientDashboardData";
import { Appointment } from "@/lib/dashboard/appointmentsData";
import { logEvent } from "../../../utils/GAUtils";
import useTitanApp from "@/hooks/useTitanApp";
import { useNavigate } from "react-router-dom";
import {
  addPatientConsultation,
  setConsultationId,
  setFreeTypeMode,
  setIsFinalSummary,
  setPatientNotes,
  setSignatureDetails,
  setTranscript,
  setConsultationPaused,
  setCurrentConsultationMediaButtonMode,
  setIsConsultationEnabled,
  setPatientTranscription,
  setSelectedPatientConsultation,
  setShowPlayPauseBtn,
  setAiChatSuccessMessages,
  setAddPatientAIChatMessage,
  setPreConsultationTemplate,
  setPreConsultationTranscription,
  setisHandleStartConsultation,
  updateConsultationStatus,
  updateEventStatus,
  setPatientAIChatMessages,
  getPreConsultationByConsultationId,
  setVaccineDetails,
  updateConsultationTemplate,
  getPaymentAuditByConsultation,
  setPaymentAudit,
  setPatientTabSwitcher,
  setHasPreConsultTab,
  setSnomedCodes,
} from "@/dispatchers/patientDispatchers";
import { setIsTemplateSelected } from "@/dispatchers/templateDispatchers";
import {
  setSelectedTemplate,
  setStreamedText,
  sendAIQAService,
} from "@/dispatchers/templateDispatchers";
import { setHydratedTemplateSections } from "@/dispatchers/templateDispatchers";
import { v4 as uuidv4 } from "uuid";
import { ConsultationButtonStatus } from "../../../enum/ConsultationButtonStatus";
import { setPatientInfoTab } from "@/dispatchers/appDispatchers";
import { useConsultationsState } from "@/lib/dashboard/consultationsData";
import Skeleton from "@mui/material/Skeleton";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { deleteConsultation } from "@/lib/dashboard/consultationsData";
import { PatientConsultationWarningModal } from "./PatientConsultationWarningModalProps";
import {
  setConsultationLoadError,
  setShowPermissionModal,
  setShowPermissionMessage,
} from "@/dispatchers/appDispatchers";
import { trackFirstTimeIntercomEvent } from "@/utils/trackFirstTimeIntercomEvent";
import { checkUserPermissions } from "@/utils/checkUserPermissions";
import { accessTitanverseConsultationUpsert } from "@/data/constantStrings";
import { useNHS111Data } from "@/lib/nhs111/nhs111Data";
import { setLinkTreatmentPlanDialog } from "@/dispatchers/treatmentPlanDispatchers";
import { getCompanyDocumentsByCompanyId } from "@/dispatchers/companyDocumentsDispatchers";
import { extractSnomedCodesFromSummary } from "@/utils/extractSnomedCodesFromSummary";
import {
  setLinkedTreatmentPlan,
  clearLinkedTreatmentPlan,
} from "@/dispatchers/treatmentPlanDispatchers";
import { setShowNHS111ReferralTab } from "@/dispatchers/pharmacyServiceDispatchers";

interface ConsultationBannerProps {
  hasActiveConsultation: boolean;
  activeConsultation: ActiveConsultation;
  todaysAppointment: Appointment | null;
  isDarkMode: boolean;
  onStartConsultation?: () => void;
}

export function ConsultationBanner({
  hasActiveConsultation,
  activeConsultation,
  todaysAppointment,
  isDarkMode,
  onStartConsultation,
}: ConsultationBannerProps) {
  const navigate = useNavigate();
  const [recentAppointment, setRecentAppointment] = useState(null);
  const [showAppointmentModal, setShowAppointmentModal] = useState(false);

  const { patientState, appState, treatmentPlanState } = useTitanApp();
  const { patientActiveTreatmentPlans } = treatmentPlanState || {};
  const {
    currentPharmacy,
    currentPharmacyId,
    companyId,
    currentUser,
    isAppLoading,
  } = appState || {};
  const {
    activeConsultations,
    showPatientConsultationsLoader,
    patientActiveConsultation,
  } = useConsultationsState();
  const {
    selectedPatientDetails,
    selectedTemplate,
    hydratedTemplateSections,
    patientTranscription,
    addPatientConsultationLoader,
    patientAppointmentList,
    transcript,
    isPatientDetailsLoading,
    preConsultData,
  } = patientState || {};
  const { setNHS111ConsultationSummary } = useNHS111Data();

  const hasConsultationPermission = checkUserPermissions(
    accessTitanverseConsultationUpsert,
  );
  const handleStartConsultationClick = async () => {
    try {
      logEvent("button_click", {
        event_category: "User Interaction",
        event_label: "Patient Consultation Start",
        pharmacy_name: currentPharmacy.teamPharmacyName,
      });

      trackFirstTimeIntercomEvent({
        eventName: "consultation_entered_first_time",
        user: {
          id: currentUser.emailAddress,
          email: currentUser.emailAddress,
          name: currentUser.fullName,
        },
        properties: {
          source: "patient_dashboard",
        },
      });
    } catch (error) {}

    if (
      Array.isArray(patientActiveTreatmentPlans) &&
      patientActiveTreatmentPlans.length > 0
    ) {
      setLinkTreatmentPlanDialog(true);
      return;
    }

    if (
      Array.isArray(patientAppointmentList) &&
      patientAppointmentList.length > 0
    ) {
      const now = new Date();
      const pendingAppointments = patientAppointmentList.filter(
        (a) => a.eventStatus === 2,
      );

      if (pendingAppointments.length > 0) {
        const sortedAppointments = pendingAppointments
          .map((a) => {
            const parts = a.appointmentDate?.trim()?.split(" ");
            if (parts.length < 3) return { ...a, parsedDate: new Date(0) };

            const [_, dayStr, monthName] = parts;
            const day = Number(dayStr);
            const month = new Date(
              `${monthName} 1, ${now.getFullYear()}`,
            ).getMonth();
            const parsedDate = new Date(now.getFullYear(), month, day);
            return { ...a, parsedDate };
          })
          .sort((a, b) => a.parsedDate - b.parsedDate);

        const oldestPendingAppointment = sortedAppointments[0];
        setRecentAppointment(oldestPendingAppointment);
        setShowAppointmentModal(true);
        return;
      }
    }

    await startNewConsultation();
  };

  const startNewConsultation = async () => {
    clearLinkedTreatmentPlan();
    setHasPreConsultTab(false);
    setPaymentAudit(null);
    setFreeTypeMode(false);
    setTranscript("");
    setPatientNotes("");
    setHydratedTemplateSections(null);
    setSignatureDetails(null);
    setSelectedPatientConsultation(null);
    setPatientAIChatMessages([]);
    const newConsultationId = uuidv4();
    setConsultationId(newConsultationId);
    setisHandleStartConsultation(true);
    setIsFinalSummary(true);
    setVaccineDetails(null);

    try {
      const consultationData = {
        consultationId: newConsultationId,
        patientId: selectedPatientDetails?.patientID,
        patientName:
          (selectedPatientDetails?.foreName || "") +
          " " +
          (selectedPatientDetails?.surName || ""),
        pharmacyID: currentPharmacyId,
        serviceID: "6880e77b98b936f8365ab340",
        serviceName: "General Service",
        consultationSummary: "",
        transcription: "",
        actions: [],
        companyID: companyId,
        consultationStatus: 2,
        createdByName: currentUser?.fullName || "",
        consultationButtonStatus: ConsultationButtonStatus.InProgress,
      };

      const result = await addPatientConsultation(consultationData);
      if (result === true) {
        setPatientInfoTab(1);
        setShowNHS111ReferralTab(false);
        navigate(
          `/pharmacy/patientconsultation/${currentPharmacy.teamPharmacyID}/${selectedPatientDetails?.patientID}/${newConsultationId}`,
        );
      }
    } catch (error) {
      setConsultationLoadError(true);
      console.error("Failed to save consultation:", error);
    }
  };

  const handleUsePreviousConsultation = async () => {
    try {
      if (!recentAppointment || !recentAppointment?.patientConsultaitonId) {
        setShowAppointmentModal(false);
        startNewConsultation();
        return;
      }
      setFreeTypeMode(false);
      setTranscript("");
      setPatientNotes("");
      setConsultationId(recentAppointment.patientConsultaitonId);
      setSignatureDetails(null);
      setSelectedPatientConsultation(null);
      setisHandleStartConsultation(true);
      setIsFinalSummary(true);
      setPatientInfoTab(1);
      setIsTemplateSelected(true);
      setPatientAIChatMessages([]);
      await getPaymentAuditByConsultation({
        consultationID: recentAppointment.patientConsultaitonId,
      });
      if (recentAppointment?.preConsultationTemplate) {
        const preConsultData = await getPreConsultationByConsultationId({
          consultationId: recentAppointment.patientConsultaitonId,
        });
        setPreConsultationTemplate(preConsultData?.preConsultationTemplate);
        if (preConsultData?.preConsultationTemplate) {
          let preConsultTranscription = "";
          preConsultData?.preConsultationTemplate?.templateSections?.forEach(
            (section) => {
              section?.userControls?.forEach((control) => {
                if (control.label && control.value) {
                  preConsultTranscription += ` ${control.label} ${control.value} `;
                }
              });
            },
          );

          setPreConsultationTranscription(preConsultTranscription?.trim());
        } else {
          setPreConsultationTranscription("");
        }
      } else {
        setPreConsultationTemplate(null);
        setPreConsultationTranscription("");
      }
      navigate(
        `/pharmacy/patientconsultation/${currentPharmacy.teamPharmacyID}/${selectedPatientDetails?.patientID}/${recentAppointment.patientConsultaitonId}`,
      );
      setPatientInfoTab(1);
      setShowNHS111ReferralTab(false);
      setPatientTabSwitcher(recentAppointment?.preConsultationTemplate ? 1 : 0);

      const data = {
        consultationId: recentAppointment.patientConsultaitonId,
        actionStatus: 2,
      };
      await updateConsultationStatus(data);

      const eventData = {
        id: recentAppointment.id,
        eventStatus: 3,
      };
      await updateEventStatus(eventData);

      setShowAppointmentModal(false);
    } catch (error) {
      console.error("Error using previous consultation:", error);
      setConsultationLoadError(true);
    }
  };

  const handleArrowClick = async (patientId, consultationData) => {
    setNHS111ConsultationSummary(consultationData);
    setHasPreConsultTab(false);
    setFreeTypeMode(false);
    setIsConsultationEnabled(false);
    setHydratedTemplateSections(null);
    setSelectedPatientConsultation(consultationData);
    setConsultationId(consultationData?.consultationId);
    setPatientTranscription(consultationData?.transcription);
    setTranscript(consultationData?.transcription);
    setSelectedTemplate(consultationData?.template);

    if (consultationData?.template?.linkedDocumentId && companyId) {
      getCompanyDocumentsByCompanyId(companyId);
    }

    setSignatureDetails(consultationData?.patientConsent);
    setIsFinalSummary(consultationData?.notes === null ? false : true);
    setPatientNotes(consultationData?.notes ?? "");
    setShowPlayPauseBtn(consultationData?.consultationStatus === 3);

    const { mainSummary, snomedCodes } = extractSnomedCodesFromSummary(
      consultationData?.consultationSummary,
    );

    setStreamedText(mainSummary);
    setSnomedCodes(snomedCodes);

    setConsultationPaused(
      consultationData?.consultationStatus === 3 &&
        !!consultationData?.transcription?.trim()
        ? true
        : false,
    );

    await getPaymentAuditByConsultation({
      consultationID: consultationData?.consultationId,
    });
    setisHandleStartConsultation(false);
    if (
      consultationData?.template != null &&
      (consultationData?.consultationStatus === 2 ||
        consultationData?.consultationStatus === 3)
    ) {
      setIsTemplateSelected(true);
    }
    if (
      consultationData?.consultationButtonStatus ===
      ConsultationButtonStatus.Manual
    ) {
      setCurrentConsultationMediaButtonMode("manual");
    }
    if (consultationData?.preConsultationTemplate) {
      const preConsultDataRes = await getPreConsultationByConsultationId({
        consultationId: consultationData?.consultationId,
      });
      setPreConsultationTemplate(preConsultDataRes?.preConsultationTemplate);
      if (preConsultDataRes?.preConsultationTemplate) {
        let preConsultTranscription = "";
        preConsultDataRes?.preConsultationTemplate?.templateSections?.forEach(
          (section) => {
            section?.userControls?.forEach((control) => {
              if (control.label && control.value) {
                preConsultTranscription += ` ${control.label} ${control.value} `;
              }
            });
          },
        );

        setPreConsultationTranscription(preConsultTranscription.trim());
      } else {
        setPreConsultationTranscription("");
      }
    } else {
      setPreConsultationTemplate(null);
      setPreConsultationTranscription("");
    }
    clearLinkedTreatmentPlan();
    setShowNHS111ReferralTab(false);
    navigate(
      `/pharmacy/patientconsultation/${currentPharmacyId}/${patientId}/${consultationData?.consultationId}`,
    );
    setPatientInfoTab(1);

    setPatientTabSwitcher(consultationData?.preConsultationTemplate ? 1 : 0);
    if (consultationData?.consultationChat) {
      try {
        const parsedAiChatMessages = JSON.parse(
          consultationData.consultationChat,
        );
        setAiChatSuccessMessages();
        parsedAiChatMessages.forEach((message) => {
          setAddPatientAIChatMessage(message);
        });
      } catch (err) {
        console.error("Invalid consultationChat JSON:", err);
      }
    }
    const filledCount = countFilledControls(consultationData?.template);
    if (filledCount < 1 && consultationData?.transcription?.trim()) {
      const allUserControls =
        consultationData?.template?.templateSections?.flatMap(
          (sec) => sec.userControls,
        ) || [];

      const questions = allUserControls.map((ctrl, idx) => ({
        [`q${idx + 1}`]: ctrl.label,
      }));
      const qaResponse = await sendAIQAService({
        q: questions,
        t: consultationData?.transcription,
      });
      const updatedTemplate = fillTemplateWithQA(
        consultationData?.template,
        qaResponse,
      );
      setSelectedTemplate(updatedTemplate);
      if (updatedTemplate?.templateSections?.length) {
        await updateConsultationTemplate({
          consultationId: consultationData?.consultationId,
          payload: updatedTemplate.templateSections,
        });
      }
    } else {
      setSelectedTemplate(consultationData?.template);
    }
  };

  function countFilledControls(template) {
    let count = 0;
    template?.templateSections?.forEach((section) => {
      section.userControls.forEach((ctrl) => {
        if (ctrl.value && ctrl.value?.trim() !== "") count++;
      });
    });
    return count;
  }
  function fillTemplateWithQA(template, qaResponse) {
    let answers = {};
    try {
      answers = JSON.parse(qaResponse?.response || "{}");
    } catch (e) {
      console.error("Invalid QA response JSON", e);
    }

    let counter = 1;
    const updatedSections = template?.templateSections?.map((section) => ({
      ...section,
      userControls: section?.userControls?.map((ctrl) => {
        const key = `a${counter}`;
        const newValue =
          answers.hasOwnProperty(key) && answers[key] !== null
            ? answers[key]
            : ctrl.value;
        counter++;
        return { ...ctrl, value: newValue };
      }),
    }));

    return {
      ...template,
      templateSections: updatedSections,
    };
  }
  const handleDelete = async (consultationId: string) => {
    if (!selectedPatientDetails?.patientID) {
      console.error("❌ No patient ID found for delete");
      return;
    }

    await deleteConsultation(
      consultationId,
      selectedPatientDetails.patientID,
      companyId,
      currentPharmacyId,
    );
  };

  const effectiveDate = (c: any): string =>
    c?.startedOn &&
    c?.createdOn &&
    new Date(c.startedOn) > new Date(c.createdOn)
      ? c.startedOn
      : c?.createdOn;

  const sortedActiveConsultations = useMemo(() => {
    if (!Array.isArray(patientActiveConsultation)) return [];

    return [...patientActiveConsultation].sort(
      (a, b) =>
        new Date(effectiveDate(b)).getTime() -
        new Date(effectiveDate(a)).getTime(),
    );
  }, [patientActiveConsultation]);

  return (
    <>
      {/* --- Start Consultation Section --- */}

      <div
        className={`mb-6 rounded-xl border transition-colors duration-300 ${
          todaysAppointment
            ? isDarkMode
              ? "bg-purple-900/20 border-purple-700 text-purple-200"
              : "bg-purple-50 border-purple-200 text-purple-800"
            : isDarkMode
            ? "bg-gray-800/50 border-gray-600 text-gray-300"
            : "bg-gray-50 border-gray-200 text-gray-700"
        }`}
      >
        <div className="flex items-center justify-between p-4">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              {todaysAppointment ? (
                <>
                  <div className="w-2 h-2 bg-purple-500 rounded-full "></div>
                  <span
                    className={`text-sm font-semibold ${
                      isDarkMode ? "text-purple-200" : "text-purple-800"
                    }`}
                  >
                    Appointment Today
                  </span>
                </>
              ) : (
                <>
                  <div
                    className={`w-2 h-2 rounded-full ${
                      isDarkMode ? "bg-gray-500" : "bg-gray-400"
                    }`}
                  ></div>
                </>
              )}
            </div>
            <div
              className={`text-sm ${
                todaysAppointment
                  ? isDarkMode
                    ? "text-purple-300"
                    : "text-purple-700"
                  : isDarkMode
                  ? "text-gray-400"
                  : "text-gray-600"
              }`}
            >
              {todaysAppointment
                ? `${todaysAppointment.type} scheduled at ${todaysAppointment.time} • Click to start appointment consultation`
                : "Start a new consultation to review patient medications, conditions, or provide healthcare guidance"}
            </div>
          </div>

          <div className="flex items-center gap-2">
            {todaysAppointment && (
              <Badge
                variant="secondary"
                className={`text-xs ${
                  isDarkMode
                    ? "bg-purple-900/30 text-purple-300 border border-purple-700"
                    : "bg-purple-100 text-purple-700"
                }`}
              >
                {todaysAppointment.status}
              </Badge>
            )}
            <Button
              size="sm"
              onClick={(e) => {
                if (!hasConsultationPermission) {
                  e.preventDefault();
                  setShowPermissionModal(true);
                  setShowPermissionMessage(
                    "You don't have permission to start consultation.",
                  );
                  return;
                }
                handleStartConsultationClick();
              }}
              disabled={
                addPatientConsultationLoader ||
                isPatientDetailsLoading ||
                isAppLoading
              }
              className={`${
                todaysAppointment
                  ? isDarkMode
                    ? "bg-purple-700 hover:bg-purple-600 text-white"
                    : "bg-purple-600 hover:bg-purple-700 text-white"
                  : isDarkMode
                  ? "bg-black text-white"
                  : "bg-black text-white"
              } ${
                !hasConsultationPermission
                  ? "opacity-50 cursor-not-allowed"
                  : ""
              }`}
            >
              {todaysAppointment ? (
                <>
                  <Calendar className="w-4 h-4 mr-2" />
                  Start Appointment
                </>
              ) : (
                <>
                  <Plus className="w-4 h-4 mr-2" />
                  Start New Consultation
                </>
              )}
            </Button>
          </div>
        </div>
      </div>

      {/* --- Active Consultations Section --- */}

      {Array.isArray(patientActiveConsultation) &&
        patientActiveConsultation.length > 0 && (
          <>
            <h2
              className={`text-lg font-semibold mb-6 transition-colors duration-300 ${
                isDarkMode ? "text-white" : "text-gray-900"
              }`}
            >
              Active Consultations
            </h2>

            {showPatientConsultationsLoader ? (
              <div className="pr-2 space-y-4 overflow-y-auto max-h-64">
                {[1, 2, 3].map((i) => (
                  <div
                    key={i}
                    className={`rounded-xl border p-4 flex items-center justify-between ${
                      isDarkMode
                        ? "bg-blue-900/20 border-blue-700"
                        : "bg-blue-50 border-blue-200"
                    }`}
                  >
                    <div className="flex items-center w-full gap-3">
                      <Skeleton
                        variant="circular"
                        width={12}
                        height={12}
                        className="flex-shrink-0"
                      />
                      <Skeleton variant="text" width="30%" height={20} />
                      <Skeleton variant="text" width="40%" height={20} />
                    </div>
                    <Skeleton variant="rectangular" width={120} height={32} />
                  </div>
                ))}
              </div>
            ) : (
              <div
                className="pr-2 space-y-4 overflow-y-auto max-h-64"
                style={{
                  maxHeight: "16rem",
                  minHeight: "8rem",
                  scrollbarWidth: "thin",
                  scrollbarColor: isDarkMode
                    ? "#334155 #1e293b"
                    : "#60a5fa #f1f5f9",
                }}
              >
                {sortedActiveConsultations.map((consultation) => (
                  <div
                    key={consultation._id}
                    className={`rounded-xl border transition-colors duration-300 ${
                      isDarkMode
                        ? "bg-blue-900/20 border-blue-700 text-blue-200"
                        : "bg-blue-50 border-blue-200 text-blue-800"
                    }`}
                  >
                    <div className="flex items-center justify-between p-4">
                      <div className="flex items-center gap-3">
                        <div className="flex items-center gap-2">
                          <div className="w-2 h-2 bg-green-500 rounded-full "></div>
                          <span
                            className={`text-sm font-semibold ${
                              isDarkMode ? "text-blue-200" : "text-blue-800"
                            }`}
                          >
                            Active Consultation
                          </span>
                        </div>

                        {consultation.templateName && (
                          <div
                            className={`text-sm ${
                              isDarkMode ? "text-blue-300" : "text-blue-700"
                            }`}
                          >
                            {consultation.templateName} •
                          </div>
                        )}

                        <div
                          className={`text-sm ${
                            isDarkMode ? "text-blue-300" : "text-blue-700"
                          }`}
                        >
                          Started{" "}
                          {dayjs(effectiveDate(consultation)).format(
                            "DD MMM YYYY, HH:mm",
                          )}
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        <Badge
                          variant="secondary"
                          className={`text-xs ${
                            isDarkMode
                              ? "bg-green-900/30 text-green-300 border border-green-700"
                              : "bg-green-100 text-green-700"
                          }`}
                        >
                          {consultation.status}
                        </Badge>

                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={(e) => {
                                if (!hasConsultationPermission) {
                                  e.preventDefault();
                                  setShowPermissionMessage(
                                    "You don't have permission to delete this consultation",
                                  );
                                  setShowPermissionModal(true);
                                  return;
                                }
                              }}
                            >
                              <Trash className="w-4 h-4 mr-2" />
                              Delete Consultation
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>
                                Delete active consultation?
                              </AlertDialogTitle>
                              <AlertDialogDescription>
                                This will permanently remove this consultation
                                and any associated transcription data. This
                                action cannot be undone.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction
                                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                onClick={() =>
                                  handleDelete(consultation.consultationId)
                                }
                              >
                                Delete
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>

                        <Button
                          onClick={() => {
                            if (!hasConsultationPermission) {
                              setShowPermissionMessage(
                                "You don't have permission to continue this consultation",
                              );
                              setShowPermissionModal(true);
                              return;
                            }
                            handleArrowClick(
                              consultation?.patientId,
                              consultation,
                            );
                          }}
                          size="sm"
                          className={`${
                            isDarkMode
                              ? "bg-blue-700 hover:bg-blue-600 text-white"
                              : "bg-blue-600 hover:bg-blue-700 text-white"
                          }`}
                        >
                          <Eye className="w-4 h-4 mr-2" />
                          Continue Consultation
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      {showAppointmentModal && recentAppointment && (
        <PatientConsultationWarningModal
          open={showAppointmentModal}
          title="Existing Appointment Found"
          message={`You have a pending appointment for ${
            recentAppointment.serviceName
          } on ${
            recentAppointment.appointmentDate
          } at ${recentAppointment.appointmentTime ||
            ""}. Would you like to continue with that consultation or start a new one?`}
          onStartNew={async () => {
            setShowAppointmentModal(false);
            await startNewConsultation();
          }}
          onStartPrevious={handleUsePreviousConsultation}
          onCancel={() => setShowAppointmentModal(false)}
        />
      )}
    </>
  );
}
